/**
 * `createGame` — dealing a new game (CR §2.1–§2.2, SPEC.md §4.4).
 *
 * The setup is plain data (`GameSetup`: deck lists, seed, options) precisely so it can be
 * the head of the action log and replay byte-for-byte. The card catalog is passed
 * alongside rather than inside it — it is the same object for every game and never
 * serializes.
 */
import { createBagState } from './bag.ts';
import type { CardCatalog } from './cards.ts';
import { isSupportedCard, requireDefinition } from './cards.ts';
import { advance, EngineError } from './engine.ts';
import type { InstanceId, PlayerIndex } from './ids.ts';
import { createRngState, nextIntBelow } from './rng.ts';
import type { CardInstance, GameState, PlayerState, RuleOptions } from './state.ts';
import { DEFAULT_RULE_OPTIONS } from './state.ts';
import { drawCards, emit, shuffleDeck } from './zones.ts';

/** Everything needed to deal a game, and nothing that can't be serialized. */
export interface GameSetup {
  /** One deck list per player, as LorcanaJSON ids — one entry per physical card. */
  readonly decks: readonly (readonly number[])[];
  /** Seeds the in-state RNG. Any uint32. */
  readonly seed: number;
  /** Who goes first. Omitted means the RNG picks, as the CR requires (§2.1.2). */
  readonly startingPlayer?: PlayerIndex;
  /** Overrides for the rule knobs; defaults are constructed Lorcana. */
  readonly rules?: Partial<RuleOptions>;
}

/** One card a deck may not contain, named the way a player would recognise it. */
export interface UnsupportedCard {
  readonly defId: number;
  /** `name - version` as printed, e.g. `Ariel - On Human Legs`. */
  readonly fullName: string;
}

/**
 * A deck contained cards the engine cannot play — game start is where that blocks.
 *
 * The offenders are **named**, not just numbered (SPEC.md §5.3, ticket 14): the deck builder
 * shows this message straight through, and "Elsa - Spirit of Winter (42)" is a card a player
 * can go and take out of their deck, where "42" is not.
 */
export class UnsupportedCardsError extends EngineError {
  /** The offending LorcanaJSON ids, ascending. */
  readonly defIds: readonly number[];
  /** The same cards with their printed names, in the same order. */
  readonly cards: readonly UnsupportedCard[];

  constructor(cards: readonly UnsupportedCard[]) {
    super(
      'The engine cannot play these cards yet: ' +
        `${cards.map((card) => `${card.fullName} (${card.defId})`).join(', ')}. ` +
        'Unimplemented cards hard-block game start (SPEC.md §5.3).',
    );
    this.cards = cards;
    this.defIds = cards.map((card) => card.defId);
  }
}

/**
 * Deal a new game and run it to its first decision — altering the starting player's hand.
 *
 * Refuses decks containing cards outside the engine's current coverage (ticket 04: vanilla
 * characters). Deck *construction* legality — 60 cards, ≤2 inks, ≤4 copies — is the
 * caller's business: `@lorcana/card-data`'s `validateDeckList` is the authority, and
 * `isUsableDeck` is what rejects the precons with known upstream data bugs.
 */
export function createGame(setup: GameSetup, catalog: CardCatalog): GameState {
  if (setup.decks.length < 2) {
    throw new EngineError(`A game needs at least 2 players, got ${setup.decks.length}`);
  }

  const rules: RuleOptions = { ...DEFAULT_RULE_OPTIONS, ...setup.rules };
  const unsupported = new Map<number, string>();
  for (const deck of setup.decks) {
    if (deck.length === 0) throw new EngineError('A player was dealt an empty deck');
    for (const defId of deck) {
      const def = requireDefinition(catalog, defId);
      if (!isSupportedCard(def, catalog)) unsupported.set(defId, def.fullName);
    }
  }
  if (unsupported.size > 0) {
    throw new UnsupportedCardsError(
      [...unsupported]
        .sort((a, b) => a[0] - b[0])
        .map(([defId, fullName]) => ({ defId, fullName })),
    );
  }

  const instances: CardInstance[] = [];
  const players: PlayerState[] = setup.decks.map((deck, index) => {
    const deckIds: InstanceId[] = [];
    for (const defId of deck) {
      const id = instances.length;
      instances.push({
        id,
        owner: index,
        controller: index,
        defId,
        zone: 'deck',
        exerted: false,
        damage: 0,
        drying: false,
        location: null,
        knownTo: 0,
      });
      deckIds.push(id);
    }
    return {
      index,
      deck: deckIds,
      hand: [],
      play: [],
      inkwell: [],
      discard: [],
      under: [],
      looking: [],
      lore: 0,
      inksThisTurn: 0,
      cardsInkedThisTurn: 0,
      cardsDrawnThisTurn: 0,
      cardsPutIntoDiscardThisTurn: 0,
      cardsDiscardedThisTurn: 0,
      alteredHand: false,
    };
  });

  const rng = createRngState(setup.seed);
  const startingPlayer =
    setup.startingPlayer ?? nextIntBelow(rng, players.length);
  if (startingPlayer < 0 || startingPlayer >= players.length) {
    throw new EngineError(`No such starting player: ${startingPlayer}`);
  }

  const state: GameState = {
    catalog,
    rules,
    instances,
    players,
    startingPlayer,
    activePlayer: startingPlayer,
    turn: 1,
    phase: 'setup',
    rng,
    // The bottom frame is permanent: stepping it starts each turn.
    stack: [{ kind: 'game' }],
    bag: createBagState(),
    modifiers: [],
    stacks: [],
    costModifiers: [],
    abilityUses: [],
    questedThisTurn: [],
    challengedThisTurn: [],
    wasChallengedThisTurn: [],
    playedThisTurn: [],
    banishedInChallengeThisTurn: [],
    cardsPutUnderThisTurn: [],
    leftDiscardThisTurn: [],
    banishedThisTurn: [],
    damagedThisTurn: [],
    damageRemovedThisTurn: players.map(() => 0),
    pending: { kind: 'game-over', player: -1, options: [] },
    events: [],
    result: null,
  };

  emit(state, {
    type: 'game-started',
    startingPlayer,
    handSize: rules.startingHandSize,
  });
  for (const player of state.players) shuffleDeck(state, player.index);
  for (const player of turnOrder(state)) drawCards(state, player, rules.startingHandSize);
  // Hands are altered in turn order, so the starting player's frame goes on top last.
  for (const player of [...turnOrder(state)].reverse()) {
    state.stack.push({ kind: 'mulligan', player, selected: [] });
  }

  advance(state);
  return state;
}

function turnOrder(state: GameState): PlayerIndex[] {
  const order: PlayerIndex[] = [];
  for (let i = 0; i < state.players.length; i += 1) {
    order.push((state.startingPlayer + i) % state.players.length);
  }
  return order;
}
